import momentTZ from "moment-timezone";
import {DEFAULT_ORGANIZATION_TIMEZONE} from "../timezone";

type DateInput = Date | string;

/**
 * Letzter Tag, an dem Mollie eine Überweisung noch zuordnet ("Überweisungsdaten
 * gültig bis"). Mollie setzt `expiresAt` auf den Tag NACH dem wirksamen dueDate
 * (live durchgehend 04:00 UTC); an diesem Morgen ist die Referenz bereits
 * abgelaufen (#1036). Abgezogen wird ein Kalendertag in der Tenant-Zeitzone,
 * nicht 24 Stunden, sonst kippt das Datum über eine Zeitumstellung.
 *
 * Gilt nur für direkte Überweisungen (`tr_`), nicht für den Ablauf eines
 * Zahlungslinks.
 */
export const resolveMollieBanktransferLastValidDay = (expiresAt: DateInput, timezone?: string | null): Date =>
    momentTZ(expiresAt)
        .tz(timezone || DEFAULT_ORGANIZATION_TIMEZONE)
        .subtract(1, "day")
        .toDate();

/**
 * Letzter gültiger Tag als Text ohne Uhrzeit. Formatiert in der Tenant-Zeitzone,
 * nicht in der des Browsers oder des Prozesses, damit der Tag überall derselbe ist.
 */
export const formatMollieBanktransferLastValidDay = (expiresAt: DateInput, format: string = "L", timezone?: string | null): string => {
    const tz = timezone || DEFAULT_ORGANIZATION_TIMEZONE;

    return momentTZ(resolveMollieBanktransferLastValidDay(expiresAt, tz))
        .tz(tz)
        .format(format);
};

/**
 * Angezeigte Fälligkeit einer Mollie-Überweisung ("Fällig bis" / "Zahlbar bis"):
 * der Zahltermin der Rate, aber nie vor `notBefore` (Rechnungsdatum, B-3310) und
 * nie nach dem letzten gültigen Tag der Überweisungsdaten. Die Obergrenze gewinnt.
 * Ändert nur die Anzeige; `ScheduledPayment.paymentDue` bleibt unberührt.
 */
export const resolveMollieBanktransferDisplayDueDay = (args: {
    paymentDue: DateInput;
    notBefore?: DateInput | null;
    expiresAt?: DateInput | null;
    timezone?: string | null;
}): Date => {
    const tz = args.timezone || DEFAULT_ORGANIZATION_TIMEZONE;
    const dayKey = (value: DateInput): string =>
        momentTZ(value)
            .tz(tz)
            .format("YYYY-MM-DD");
    let due: DateInput = args.paymentDue;

    if (args.notBefore && dayKey(due) < dayKey(args.notBefore)) {
        due = args.notBefore;
    }

    if (args.expiresAt) {
        const lastValidDay = resolveMollieBanktransferLastValidDay(args.expiresAt, tz);

        if (dayKey(due) > dayKey(lastValidDay)) {
            due = lastValidDay;
        }
    }

    return momentTZ(due).toDate();
};
